/**
 * SensorSelect - Searchable dropdown for selecting sensors
 *
 * Usage:
 * <SensorSelect
 *   value={sensorHumanId}
 *   onChange={setSensorHumanId}
 *   sensors={sensors}
 *   label="Sensor"
 * />
 *
 * // Multi-select variant
 * <SensorSelect
 *   value={selectedSensorIds}
 *   onChange={setSelectedSensorIds}
 *   sensors={sensors}
 *   label="Sensors"
 *   multiple
 * />
 */

import type { SensorsSensor } from '@smarthome/api';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';

import { cn } from '@/components/ui/utils';

interface SensorSelectBaseProps {
  /** Label for the field */
  label?: string;
  /** Available sensors */
  sensors: SensorsSensor[];
  /** Placeholder text */
  placeholder?: string;
  /** Error message */
  error?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Additional classes */
  className?: string;
  /** Filter function to exclude certain sensors */
  filter?: (sensor: SensorsSensor) => boolean;
  /** Required unit symbol - only sensors with this unit will be shown */
  unitSymbol?: string;
  /** Sensor IDs to exclude from selection */
  excludeIds?: string[];
}

interface SingleSelectProps extends SensorSelectBaseProps {
  /** Single selection mode */
  multiple?: false;
  /** Currently selected sensor humanId */
  value: string | null;
  /** Called when selection changes */
  onChange: (value: string | null) => void;
}

interface MultiSelectProps extends SensorSelectBaseProps {
  /** Multi-selection mode */
  multiple: true;
  /** Currently selected sensor humanIds */
  value: string[];
  /** Called when selection changes */
  onChange: (value: string[]) => void;
}

export type SensorSelectProps = SingleSelectProps | MultiSelectProps;

export function SensorSelect(props: SensorSelectProps) {
  const {
    label,
    sensors,
    placeholder = 'Select sensor...',
    error,
    disabled,
    className,
    filter,
    unitSymbol,
    excludeIds = [],
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter and group sensors
  const filteredSensors = useMemo(() => {
    let result = sensors;

    // Apply unit symbol filter
    if (unitSymbol) {
      result = result.filter((s) => s.unitSymbol === unitSymbol);
    }

    // Apply custom filter
    if (filter) {
      result = result.filter(filter);
    }

    // Apply exclude filter
    if (excludeIds.length > 0) {
      result = result.filter((s) => !excludeIds.includes(s.humanId ?? ''));
    }

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (s) =>
          (s.name?.toLowerCase().includes(searchLower) ?? false) ||
          (s.humanId?.toLowerCase().includes(searchLower) ?? false)
      );
    }

    return result;
  }, [sensors, unitSymbol, filter, excludeIds, search]);

  // Group sensors by provider
  const groupedSensors = useMemo(() => {
    const groups: Record<string, SensorsSensor[]> = {};
    filteredSensors.forEach((s) => {
      const group = s.provider ?? 'Other';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(s);
    });
    // Sort groups alphabetically
    return Object.fromEntries(Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)));
  }, [filteredSensors]);

  // Get selected sensors for display
  const selectedSensors = useMemo(() => {
    if (props.multiple) {
      return props.value.map((id) => sensors.find((s) => s.humanId === id)).filter(Boolean);
    }
    const sensor = sensors.find((s) => s.humanId === props.value);
    return sensor ? [sensor] : [];
  }, [props.multiple, props.value, sensors]);

  // Calculate if menu should open upward
  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const triggerRect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const estimatedMenuHeight = 320;

    setOpenUpward(spaceBelow < estimatedMenuHeight && spaceAbove > estimatedMenuHeight);
  }, []);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Focus search input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (sensor: SensorsSensor) => {
    const sensorId = sensor.humanId ?? '';

    if (props.multiple) {
      if (props.value.includes(sensorId)) {
        props.onChange(props.value.filter((id) => id !== sensorId));
      } else {
        props.onChange([...props.value, sensorId]);
      }
    } else {
      props.onChange(sensorId);
      setIsOpen(false);
      setSearch('');
    }
  };

  const handleRemove = (sensorId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.multiple) {
      props.onChange(props.value.filter((id) => id !== sensorId));
    } else {
      props.onChange(null);
    }
  };

  const isSelected = (sensor: SensorsSensor) => {
    if (props.multiple) {
      return props.value.includes(sensor.humanId ?? '');
    }
    return props.value === sensor.humanId;
  };

  // Display text for single select
  const displayText = useMemo(() => {
    if (props.multiple) return null;
    if (!props.value) return placeholder;
    const sensor = sensors.find((s) => s.humanId === props.value);
    return sensor?.name ?? props.value;
  }, [props.multiple, props.value, placeholder, sensors]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          {label}
        </label>
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={cn(
          'flex min-h-[42px] w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
          'border-gray-300 bg-gray-50 hover:border-gray-400',
          'dark:border-white/[0.06] dark:bg-[#18181b] dark:hover:border-white/[0.1]',
          error && 'border-red-500 dark:border-red-500',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <div className="flex flex-1 flex-wrap items-center gap-1.5">
          {props.multiple && selectedSensors.length > 0 ? (
            // Multi-select: show chips
            selectedSensors.map((sensor) => (
              <span
                key={sensor?.humanId}
                className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
              >
                {sensor?.name ?? sensor?.humanId}
                <button
                  type="button"
                  onClick={(e) => handleRemove(sensor?.humanId ?? '', e)}
                  className="rounded-full p-0.5 hover:bg-blue-200 dark:hover:bg-blue-500/30"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))
          ) : props.multiple ? (
            <span className="text-gray-500 dark:text-zinc-500">{placeholder}</span>
          ) : (
            <span
              className={cn(
                selectedSensors.length > 0
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-zinc-500'
              )}
            >
              {displayText}
            </span>
          )}
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 flex-shrink-0 text-gray-500 transition-transform dark:text-zinc-500',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={cn(
            'absolute left-0 z-50 w-full max-w-md rounded-lg border border-gray-200 bg-white shadow-xl dark:border-white/[0.06] dark:bg-[#18181b]',
            openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
          )}
        >
          {/* Search input */}
          <div className="border-b border-gray-200 p-2 dark:border-white/[0.06]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-zinc-500" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search sensors..."
                className="w-full rounded-md border-0 bg-gray-50 py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zinc-800 dark:text-white dark:placeholder-zinc-500"
              />
            </div>
          </div>

          {/* Sensor list */}
          <div className="max-h-64 overflow-y-auto py-1">
            {Object.entries(groupedSensors).map(([group, items]) => {
              if (items.length === 0) return null;
              return (
                <div key={group}>
                  <div className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                    {group}
                  </div>
                  {items.map((sensor) => (
                    <button
                      key={sensor.id}
                      type="button"
                      onClick={() => handleSelect(sensor)}
                      className={cn(
                        'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.04]',
                        isSelected(sensor) && 'bg-blue-50 dark:bg-blue-500/10'
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-gray-900 dark:text-white">
                          {sensor.name ?? sensor.humanId}
                        </div>
                        <div className="flex items-center gap-2 truncate text-xs text-gray-500 dark:text-zinc-500">
                          <span className="truncate font-mono">{sensor.humanId}</span>
                          {sensor.unitSymbol && (
                            <>
                              <span>•</span>
                              <span>{sensor.unitSymbol}</span>
                            </>
                          )}
                        </div>
                      </div>
                      {isSelected(sensor) && (
                        <Check className="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                      )}
                    </button>
                  ))}
                </div>
              );
            })}
            {filteredSensors.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-zinc-500">
                No sensors found
              </div>
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
